import type { FilterOption } from '@/scopes/search/types'
import { useIntl } from '@/intl'
import * as DepartureAirportFilterPrimitive from '@/scopes/common/components/Filters/DepartureAirportFilterPrimitive'
import { Content } from '@/scopes/common/components/Filters/DepartureAirportFilterPrimitive'
import { mapOptions } from '@/scopes/common/components/Filters/GroupedAirportFilter/mapper'
import { OptionGroup } from '@/scopes/common/components/Filters/GroupedAirportFilter/OptionGroup'
import { GroupOption } from '@/scopes/common/components/Filters/GroupedAirportFilter/types'
import { Filter } from '@/scopes/search/components/QuickSearchBar/Filter'
import { FilterSummary } from '@/scopes/search/components/QuickSearchBar/FilterSummary'
import { useQuickSearchStore } from '@/scopes/search/components/QuickSearchBar/store'
import { useMultiSelectFilter } from '@/scopes/search/search/domain/hooks'
import { CheckboxState } from '@sunwebgroup/ui-components/uds/index'
import { equals, reject } from 'ramda'
import * as React from 'react'
import { PropsWithChildren } from 'react'

export function DepartureAirportFilterComponent({
  id,
  options,
  selectedOptions,
  disabled,
  asSheet,
  onConfirm,
}: {
  id: string
  options: FilterOption<string>[]
  selectedOptions: FilterOption<string>[]
  disabled?: boolean
  asSheet?: boolean
  onConfirm: (selection: string[]) => void
}) {
  const { setActiveItem } = useQuickSearchStore()
  const [localSelectedValues, setLocalSelectedValues] = React.useState(selectedOptions.map(option => option.value))
  const onSelectedValueChange = (newSelectedValue: string[]) => {
    setLocalSelectedValues(newSelectedValue)
  }

  React.useEffect(() => {
    setLocalSelectedValues(selectedOptions.map(option => option.value))
  }, [selectedOptions])

  const handleConfirm = () => {
    setActiveItem(null)
    onConfirm(localSelectedValues)
  }

  return (
    <CommonFilter
      id={id}
      asSheet={asSheet}
      handleClose={handleConfirm}
    >
      <DepartureAirportFilterPrimitive.Root
        options={options}
        selectedValues={localSelectedValues}
        onSelectedValueChange={onSelectedValueChange}
      />
      <FilterFooter disabled={disabled} handleClose={handleConfirm} />
    </CommonFilter>
  )
}

export function GroupedDepartureAirportFilterComponent({
  disabled,
  onConfirm,
  options,
  selectedOptions,
  asSheet,
  id,
}: {
  onConfirm: (selection: string[]) => void
  options: FilterOption<string>[]
  selectedOptions: FilterOption<string>[]
  disabled?: boolean
  asSheet?: boolean
  id: string
}) {
  const { setActiveItem } = useQuickSearchStore()
  const [localSelectedValues, setLocalSelectedValues] = React.useState(selectedOptions.map(option => option.value))

  const onChange = React.useCallback(
    (checked: CheckboxState, options: GroupOption[]) => {
      if (checked) {
        const notCheckedOptionsIds = options
          .filter(option => !localSelectedValues.includes(option.id))
          .map(option => option.id)
        setLocalSelectedValues([...localSelectedValues, ...notCheckedOptionsIds])
      }
      else {
        const checkedOptionsIds = options
          .filter(option => localSelectedValues.includes(option.id))
          .map(option => option.id)
        setLocalSelectedValues(localSelectedValues.filter(val => !checkedOptionsIds.includes(val)))
      }
    },
    [localSelectedValues],
  )

  const onChangeOption = React.useCallback(
    ({ id: optionId, checked }: { id: string, checked: CheckboxState }) => {
      checked
        ? setLocalSelectedValues([...localSelectedValues, optionId])
        : setLocalSelectedValues(reject(equals(optionId), localSelectedValues))
    },
    [localSelectedValues],
  )

  React.useEffect(() => {
    setLocalSelectedValues(selectedOptions.map(option => option.value))
  }, [selectedOptions])

  const handleClose = () => {
    setActiveItem(null)
    onConfirm(localSelectedValues)
  }

  const mappedOptions = React.useMemo(() => mapOptions(options), [options])

  return (
    <CommonFilter
      id={id}
      asSheet={asSheet}
      handleClose={handleClose}
    >
      <Content>
        {mappedOptions.map(({
          id: value,
          value: text,
          children: options = [],
        }) => (
          <OptionGroup
            key={value}
            value={value}
            text={text}
            options={options}
            selectedValues={localSelectedValues}
            onChange={checked => onChange(checked, options)}
            onChangeOption={onChangeOption}
          />
        ),
        )}
      </Content>
      <FilterFooter disabled={disabled} handleClose={handleClose} />
    </CommonFilter>
  )
}

function CommonFilter({ id, asSheet, handleClose, children }: PropsWithChildren<{ id: string, handleClose: () => void, asSheet?: boolean }>) {
  const intl = useIntl()
  const { selectedOptions } = useMultiSelectFilter('DepartureAirport')
  const formattedSelectedValues = getFormattedSelectedValues(selectedOptions)
  return (
    <Filter
      selectionFormattedText={formattedSelectedValues}
      id={id}
      placeholder={intl.formatMessage({
        id: 'quicksearch_filter_departure_airport_placeholder',
      })}
      title={intl.formatMessage({
        id: 'quicksearch_filter_departure_airport_title',
      })}
      onConfirm={handleClose}
      asSheet={asSheet}
      data-qa="departure-airport-filter"
      hasPadding={false}
    >
      {children}
    </Filter>
  )
}

function FilterFooter({ handleClose, disabled }: { disabled?: boolean, handleClose: () => void }) {
  const intl = useIntl()
  return (
    <DepartureAirportFilterPrimitive.Footer>
      <DepartureAirportFilterPrimitive.CloseButton $type="primary" onClick={handleClose} disabled={disabled}>
        {intl.formatMessage({ id: 'quicksearch_save_button_text' })}
      </DepartureAirportFilterPrimitive.CloseButton>
    </DepartureAirportFilterPrimitive.Footer>
  )
}

export function DepartureAirportFilterSummary() {
  const { selectedOptions } = useMultiSelectFilter('DepartureAirport')
  const formattedSelectedValues = getFormattedSelectedValues(selectedOptions)
  return (
    <FilterSummary
      icon="AirplaneToRight"
      texts={{
        long: formattedSelectedValues,
      }}
    />
  )
}

function getFormattedSelectedValues(selectedOptions: FilterOption<string>[]) {
  return selectedOptions.map(o => o.value).join(', ')
}
